import { Button } from '@react-spectrum/s2'
import { useEffect, useId, useRef, useState } from 'react'
import { AlertCircle, ArrowUp, Square } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSubmit: (message: string) => void
  onCancel?: () => void
  isLoading?: boolean
  isDisabled?: boolean
  onDisabledClick?: () => void
  disabledCtaText?: string
  showSubmitButton?: boolean
  className?: string
  draftValue?: string
  draftKey?: string | number
}

export function ChatInput({
  onSubmit,
  onCancel,
  isLoading,
  isDisabled,
  onDisabledClick,
  disabledCtaText = 'Upgrade',
  showSubmitButton = true,
  className,
  draftValue,
  draftKey,
}: ChatInputProps) {
  const [input, setInput] = useState('')
  const helperTextId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const hasSubmittableInput = input.trim().length > 0
  const canShowSubmitButton = showSubmitButton && !isDisabled && !isLoading
  const canShowCancelButton =
    showSubmitButton && !isDisabled && isLoading && onCancel

  const syncTextareaHeight = () => {
    const textarea =
      containerRef.current?.querySelector<HTMLTextAreaElement>('textarea') ??
      null
    if (!textarea) return
    textarea.style.height = '0px'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 240)}px`
  }

  const handleSubmit = () => {
    if (!hasSubmittableInput || isLoading || isDisabled) return
    onSubmit(input)
    setInput('')
    requestAnimationFrame(() => {
      syncTextareaHeight()
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  useEffect(() => {
    syncTextareaHeight()
  }, [input])

  useEffect(() => {
    if (draftValue == null) return
    setInput(draftValue)
    requestAnimationFrame(() => {
      syncTextareaHeight()
    })
  }, [draftKey, draftValue])

  return (
    <div
      ref={containerRef}
      className={cn('relative group min-w-0 max-w-full', className)}
    >
      <div
        className={cn(
          'relative min-w-0 rounded-[1.15rem] border border-border/80 bg-background px-4 py-3 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.28)] transition-all duration-200',
          isDisabled
            ? 'bg-muted/20'
            : 'focus-within:border-blue-300/70 focus-within:shadow-[0_18px_40px_-28px_rgba(37,99,235,0.22)]',
        )}
      >
        <textarea
          id="chat-message-input"
          name="message"
          aria-label="Ask about building regulations"
          aria-describedby={isDisabled ? undefined : helperTextId}
          autoComplete="off"
          spellCheck="false"
          rows={1}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isDisabled ? 'Chat usage limit reached.' : 'Ask a question...'
          }
          disabled={isLoading || isDisabled}
          className={cn(
            'block min-h-[3.5rem] w-full max-h-[240px] resize-none overflow-y-auto border-0 bg-transparent px-0 py-0 pb-11 text-[15px] leading-6 text-foreground outline-none placeholder:text-muted-foreground/90',
            isDisabled && 'cursor-not-allowed text-muted-foreground',
          )}
        />
        {canShowSubmitButton || canShowCancelButton ? (
          <div className="absolute bottom-3 right-3 z-10 flex items-center justify-end">
            <Button
              onPress={canShowCancelButton ? onCancel : handleSubmit}
              isDisabled={canShowSubmitButton && !hasSubmittableInput}
              size="S"
              variant="primary"
              aria-label={
                canShowCancelButton ? 'Stop response' : 'Send message'
              }
              className="relative z-10 rounded-full"
            >
              {canShowCancelButton ? (
                <>
                  <Square className="h-3.5 w-3.5 fill-current" />
                  <span className="sr-only">Stop</span>
                </>
              ) : (
                <>
                  <ArrowUp className="h-4 w-4" />
                  <span className="sr-only">Send</span>
                </>
              )}
            </Button>
          </div>
        ) : null}
      </div>
      {isDisabled && (
        <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-border/80 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 font-medium">
            <AlertCircle className="h-3.5 w-3.5 text-primary" />
            <span>Usage limit reached for your current plan.</span>
          </div>
          <Button type="button" size="S" onPress={() => onDisabledClick?.()}>
            {disabledCtaText}
          </Button>
        </div>
      )}
      {!isDisabled && (
        <p
          id={helperTextId}
          className="mt-2 hidden pr-2 text-right text-[11px] text-muted-foreground sm:block"
        >
          Press{' '}
          <kbd className="rounded border border-border bg-secondary px-1 font-sans">
            Enter
          </kbd>{' '}
          to send,{' '}
          <kbd className="rounded border border-border bg-secondary px-1 font-sans">
            Shift+Enter
          </kbd>{' '}
          for a new line
        </p>
      )}
    </div>
  )
}
